import { useFragment } from "@/generated";
import { SlideFieldsFragmentDoc, UnlinkSlideDocument } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for unlinking a slide and updating the Apollo cache.
 *
 * Converts a linked slide copy to an independent slide by:
 * 1. Setting isLinkedCopy to false
 *
 * **Cache Strategy:**
 * Uses cache.modify() to update the slide's fields in the normalized cache.
 * Apollo automatically propagates changes to all queries referencing this slide.
 *
 * **Related Hooks:**
 * - useDuplicateSlide: Creates linked copies
 *
 */

export function useUnlinkSlide() {
  const [UnlinkSlideMutation, { loading }] = useMutation(UnlinkSlideDocument);

  const unlinkSlide = async (slideId: string, originalSlideId: string | null) => {
    return withToast(
      async () => {
        const result = await UnlinkSlideMutation({
          variables: {
            slideId,
          },
          update(cache, { data }) {
            if (!data?.unlinkSlide) return;

            // Update the unlinked slide
            cache.modify({
              id: `Slide:${slideId}`,
              fields: {
                isLinkedCopy: () => false,
              },
            });

            // Update the original slide's linkedCopies
            if (originalSlideId) {
              const originalId = cache.identify({
                __typename: "Slide",
                id: originalSlideId,
              });
              if (originalId) {
                cache.modify({
                  id: `Slide:${originalSlideId}`,
                  fields: {
                    linkedCopies: (existingData, { readField }) => {
                      return existingData.filter(
                        (ref: Reference) => readField("id", ref) !== slideId
                      );
                    },
                  },
                });
              }
            }
          },
        });

        const maskedSlide = result.data?.unlinkSlide;
        const unlinkedSlide = maskedSlide
          ? useFragment(SlideFieldsFragmentDoc, maskedSlide)
          : undefined;

        return unlinkedSlide;
      },
      {
        loading: "Unlinking slide...",
        success: "Successfully unlinked slide",
        error: "Failed to unlink slide. Please try again.",
      },
      "unlinkSlide"
    );
  };

  return {
    unlinkSlide,
    loading,
  };
}
